import React from 'react';
import { FileText, Building, Users, RefreshCw, AlertTriangle } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Contract } from '@app-types';
import { formatCurrency } from '@utils/currencyUtils';
import { formatCalendarDate } from '@utils/dateUtils';
import { CONTRACT_AMOUNT_LABEL } from '@utils/contractLabels';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import Tooltip from '@components/common/Tooltip';
import { contractStatusToVariant, tierToVariant } from '@utils/badgeMappers';
import { getContractStatusLabel } from '@utils/referenceCatalog';
import { InfoCard, InfoCardGrid, SupplierLink } from '@components/common';
import TierBadge from '@components/common/TierBadge';
import ScopeBadge from '@components/common/ScopeBadge';
import { usePermissions } from '@contexts/AuthContext';
import { contractService } from '@services';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import styles from './ContractInfo.module.css';

interface ContractInfoProps {
  contract: Contract;
  assessmentsCount?: number;
  activeAssessmentsCount?: number;
  onViewSupplier?: (supplierId: number) => void;
  onChangeSupplier?: () => void;
  canChangeSupplier?: boolean;
}

const ContractInfo: React.FC<ContractInfoProps> = ({
  contract,
  assessmentsCount = 0,
  activeAssessmentsCount = 0,
  onViewSupplier,
  onChangeSupplier,
  canChangeSupplier = false
}) => {
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const isManual = contract.assessment_scope_mode === 'Manual';
  const scopeValue = contract.assessment_scope?.toLowerCase() === 'exclude' ? 'exclude' : 'include';
  const hasPending = Boolean(contract.pending_assessment_scope);

  const syncContractCache = (updated: Contract): void => {
    queryClient.setQueryData(['contract', contract.id], updated);
    queryClient.setQueryData(['contract', String(contract.id)], updated);
    void queryClient.invalidateQueries({ queryKey: ['contracts'] });
    void queryClient.invalidateQueries({ queryKey: ['pending-scope'] });
    void queryClient.invalidateQueries({ queryKey: ['program-lead-quick-actions'] });
    void queryClient.invalidateQueries({ queryKey: ['audit-history', 'contract', contract.id] });
  };

  const applyMutation = useMutation({
    mutationFn: () => contractService.applyPendingScope(contract.id),
    onSuccess: (updated) => {
      showToast.success('Pending scope applied');
      syncContractCache(updated);
    },
    onError: (error: unknown) => {
      showToast.error(getApiErrorMessage(error, 'Failed to apply pending scope'));
    },
  });

  const dismissMutation = useMutation({
    mutationFn: () => contractService.dismissPendingScope(contract.id),
    onSuccess: (updated) => {
      showToast.success('Pending scope dismissed');
      syncContractCache(updated);
    },
    onError: (error: unknown) => {
      showToast.error(getApiErrorMessage(error, 'Failed to dismiss pending scope'));
    },
  });

  const isScopeActionPending = applyMutation.isPending || dismissMutation.isPending;
  return (
    <div className={styles.contractInfoSimple}>
      {hasPending && (
        <div className={styles.pendingBanner} data-test-id="pending-scope-banner">
          <AlertTriangle size={16} />
          <div className={styles.pendingBannerContent}>
            <strong>Pending scope change</strong>
            <span>
              Calculated {contract.pending_assessment_scope} is waiting because this contract has an active assessment.
              {contract.pending_scope_reason ? ` ${contract.pending_scope_reason}` : ''}
            </span>
          </div>
          {isAdmin && (
            <div className={styles.pendingBannerActions}>
              <Button
                variant="primary"
                size="sm"
                onClick={() => applyMutation.mutate()}
                disabled={isScopeActionPending}
                loading={applyMutation.isPending}
                dataTestId="apply-pending-scope"
              >
                Apply
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => dismissMutation.mutate()}
                disabled={isScopeActionPending}
                loading={dismissMutation.isPending}
                dataTestId="dismiss-pending-scope"
              >
                Dismiss
              </Button>
            </div>
          )}
        </div>
      )}
      <InfoCardGrid>
        {/* Basic Info Card */}
        <InfoCard>
          <InfoCard.Header>
            <InfoCard.Title icon={<FileText size={20} />}>Contract Information</InfoCard.Title>
          </InfoCard.Header>
          <InfoCard.Content>
            <InfoCard.Row>
              <InfoCard.Label>Contract Number:</InfoCard.Label>
              <InfoCard.Value>{contract.contract_number}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Name:</InfoCard.Label>
              <InfoCard.Value>{contract.name}</InfoCard.Value>
            </InfoCard.Row>
            {contract.description && (
              <InfoCard.Row>
                <InfoCard.Label>Description:</InfoCard.Label>
                <InfoCard.Value>{contract.description}</InfoCard.Value>
              </InfoCard.Row>
            )}
            <InfoCard.Row>
              <InfoCard.Label>Status:</InfoCard.Label>
              <Badge variant={contractStatusToVariant(contract.status)}>{getContractStatusLabel(contract.status)}</Badge>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Start Date:</InfoCard.Label>
              <InfoCard.Value>{formatCalendarDate(contract.start_date, '-')}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract End Date:</InfoCard.Label>
              <InfoCard.Value>{formatCalendarDate(contract.end_date, '-')}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>{CONTRACT_AMOUNT_LABEL}:</InfoCard.Label>
              <InfoCard.Value>{formatCurrency(contract.amount || contract.value)}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Type:</InfoCard.Label>
              <InfoCard.Value>{contract.contract_type || '-'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Cost Center:</InfoCard.Label>
              <InfoCard.Value>{contract.cost_center || '-'}</InfoCard.Value>
            </InfoCard.Row>
            {contract.tier && (
              <InfoCard.Row>
                <InfoCard.Label>Tier:</InfoCard.Label>
                <TierBadge variant={tierToVariant(contract.tier)}>{contract.tier}</TierBadge>
              </InfoCard.Row>
            )}
            <InfoCard.Row>
              <InfoCard.Label>Scope:</InfoCard.Label>
              <InfoCard.Value>
                <span className={styles.scopeValue}>
                  <ScopeBadge scope={scopeValue} />
                  <Badge variant={isManual ? 'warning' : 'default'}>
                    {isManual ? 'Manual override' : 'Calculated'}
                  </Badge>
                </span>
              </InfoCard.Value>
            </InfoCard.Row>
            {!isManual && contract.assessment_scope_reason && (
              <InfoCard.Row>
                <InfoCard.Label>Scope reason:</InfoCard.Label>
                <InfoCard.Value>{contract.assessment_scope_reason}</InfoCard.Value>
              </InfoCard.Row>
            )}
            <InfoCard.Row>
              <InfoCard.Label>PII Access:</InfoCard.Label>
              <InfoCard.Value>{contract.pii_access ? 'Yes' : 'No'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>PHI Access:</InfoCard.Label>
              <InfoCard.Value>{contract.phi_access ? 'Yes' : 'No'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>BCI Access:</InfoCard.Label>
              <InfoCard.Value>{contract.bci_access ? 'Yes' : 'No'}</InfoCard.Value>
            </InfoCard.Row>
          </InfoCard.Content>
        </InfoCard>

        {/* Team & Supplier Card */}
        <InfoCard>
          <InfoCard.Header>
            <InfoCard.Title icon={<Users size={20} />}>Team & Supplier</InfoCard.Title>
          </InfoCard.Header>
          <InfoCard.Content>
            <InfoCard.Row>
              <InfoCard.Label>Supplier Name:</InfoCard.Label>
              <InfoCard.ValueWithAction>
                {contract.supplier_name ? (
                  <SupplierLink icon={<Building size={16} />} onClick={() => onViewSupplier?.(contract.supplier_id)}>
                    {contract.supplier_name}
                  </SupplierLink>
                ) : (
                  <InfoCard.Value>No supplier assigned</InfoCard.Value>
                )}
                {canChangeSupplier && onChangeSupplier && (
                  <Tooltip
                    content={`Cannot reassociate contract - has ${activeAssessmentsCount} active assessment${
                      activeAssessmentsCount !== 1 ? 's' : ''
                    }. Complete or cancel them first.`}
                    position="top"
                    disabled={activeAssessmentsCount === 0}
                    width="200px"
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onChangeSupplier}
                      disabled={activeAssessmentsCount > 0}
                      title={activeAssessmentsCount === 0 ? 'Change Supplier' : undefined}
                      dataTestId="contract-change-supplier-btn"
                    >
                      <RefreshCw size={14} />
                      Change
                    </Button>
                  </Tooltip>
                )}
              </InfoCard.ValueWithAction>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Division:</InfoCard.Label>
              <InfoCard.Value>{contract.division_name || contract.division || '-'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Division Lead:</InfoCard.Label>
              <InfoCard.Value>{contract.division_lead_name || '-'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Owner:</InfoCard.Label>
              <InfoCard.Value>
                {contract.contract_owners && contract.contract_owners.length > 0
                  ? contract.contract_owners.map((o) => o.name).join(', ')
                  : 'Unassigned'}
              </InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Manager:</InfoCard.Label>
              <InfoCard.Value>
                {contract.contract_managers && contract.contract_managers.length > 0
                  ? contract.contract_managers.map((m) => m.name).join(', ')
                  : 'Unassigned'}
              </InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Total Assessments:</InfoCard.Label>
              <InfoCard.Value>{assessmentsCount}</InfoCard.Value>
            </InfoCard.Row>
          </InfoCard.Content>
        </InfoCard>
      </InfoCardGrid>
    </div>
  );
};

export default ContractInfo;
